import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { useModal } from "../../context/ModalContext";
import CSSelect from "../../components/CSSelect";

const DELIVERY_PRESETS = [
  "부재 시 경비실에 맡겨주세요.",
  "부재 시 문 앞에 놓아주세요.",
  "배송 전 연락 부탁드립니다.",
  "파손 주의 부탁드립니다.",
];

const PRESET_KEY_MAP = {
  "부재 시 경비실에 맡겨주세요.": "presetLeaveSecurity",
  "부재 시 문 앞에 놓아주세요.": "presetLeaveDoor",
  "배송 전 연락 부탁드립니다.": "presetCallFirst",
  "파손 주의 부탁드립니다.": "presetFragile",
};

const API = process.env.REACT_APP_API_URL || "";

export default function MyPageAddress() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { showAlert, showConfirm } = useModal();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  async function fetchAddresses() {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API}/api/addresses?user_id=${user.id}`);
      const data = await res.json();
      setAddresses(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAddresses(); }, [user]);

  async function handleDelete(id) {
    if (!(await showConfirm(t("mypage.address.confirmDelete")))) return;
    try {
      await fetch(`${API}/api/addresses/${id}`, { method: "DELETE" });
      setAddresses((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      showAlert(t("mypage.address.deleteFailed"));
    }
  }

  function startEdit(addr) {
    setEditId(addr.id);
    setEditForm({ ...addr });
  }

  function cancelEdit() {
    setEditId(null);
    setEditForm({});
  }

  async function saveEdit() {
    try {
      const saveData = { ...editForm, user_id: user.id };
      if (saveData.delivery_memo === "직접입력") {
        saveData.delivery_memo = saveData.custom_memo || "";
      }
      delete saveData.custom_memo;
      const res = await fetch(`${API}/api/addresses/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(saveData),
      });
      if (res.ok) {
        await fetchAddresses();
        setEditId(null);
        setEditForm({});
      } else {
        showAlert(t("mypage.address.updateFailed"));
      }
    } catch (e) {
      showAlert(t("mypage.address.updateFailed"));
    }
  }

  function ef(key, val) {
    setEditForm((p) => ({ ...p, [key]: val }));
  }

  function openDaumPostcode() {
    if (!window.daum || !window.daum.Postcode) {
      const script = document.createElement("script");
      script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.onload = () => runPostcode();
      document.head.appendChild(script);
    } else {
      runPostcode();
    }
  }

  function runPostcode() {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setEditForm((p) => ({
          ...p,
          zipcode: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
      },
    }).open();
  }

  return (
    <div className="mypage-card">
      <h2>{t("mypage.address.title")}</h2>
      <p className="mypage-desc">{t("mypage.address.description")}</p>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#7C6655" }}>{t("mypage.common.loading")}</div>
      ) : addresses.length === 0 ? (
        <div className="mp-addr-empty">
          <p>{t("mypage.address.emptyTitle")}</p>
          <p>{t("mypage.address.emptyHint")}</p>
        </div>
      ) : (
        <div className="mp-addr-list">
          {addresses.map((a) => (
            <div key={a.id} className="mp-addr-card">
              {editId === a.id ? (
                /* 수정 모드 */
                <div className="mp-addr-edit">
                  <div className="mp-edit-row">
                    <label>{t("mypage.address.label")}
                      <input value={editForm.label || ""} onChange={(e) => ef("label", e.target.value)} />
                    </label>
                    <label className="mp-edit-check">
                      <input type="checkbox" checked={editForm.is_default || false} onChange={(e) => ef("is_default", e.target.checked)} />
                      {t("mypage.address.isDefault")}
                    </label>
                  </div>
                  <div className="mp-edit-row">
                    <label>{t("mypage.address.name")}
                      <input value={editForm.customer_name || ""} onChange={(e) => ef("customer_name", e.target.value)} />
                    </label>
                    <label>{t("mypage.address.phone")}
                      <input value={editForm.customer_phone || ""} onChange={(e) => ef("customer_phone", e.target.value)} />
                    </label>
                  </div>
                  <div className="mp-edit-row">
                    <label>{t("mypage.address.zipcode")}
                      <div style={{ display: "flex", gap: 6 }}>
                        <input value={editForm.zipcode || ""} readOnly style={{ flex: 1 }} />
                        <button type="button" className="mp-zip-btn" onClick={openDaumPostcode}>{t("mypage.address.searchZip")}</button>
                      </div>
                    </label>
                  </div>
                  <label>{t("mypage.address.address")}
                    <input value={editForm.address || ""} readOnly />
                  </label>
                  <label>{t("mypage.address.addressDetail")}
                    <input value={editForm.address_detail || ""} onChange={(e) => ef("address_detail", e.target.value)} />
                  </label>
                  <div className="mp-edit-field">
                    <span className="mp-edit-field-label">{t("mypage.address.deliveryMemo")}</span>
                    <CSSelect
                      block
                      value={editForm.delivery_memo || ""}
                      onChange={(v) => ef("delivery_memo", v)}
                      placeholder={t("mypage.address.selectNone")}
                      options={[
                        { value: "", label: t("mypage.address.selectNone") },
                        ...DELIVERY_PRESETS.map((p) => ({ value: p, label: t(`mypage.address.${PRESET_KEY_MAP[p]}`) })),
                        { value: "직접입력", label: t("mypage.address.customInput") },
                      ]}
                    />
                  </div>
                  {editForm.delivery_memo === "직접입력" && (
                    <input value={editForm.custom_memo || ""} onChange={(e) => ef("custom_memo", e.target.value)} placeholder={t("mypage.address.customMemoPlaceholder")} />
                  )}
                  <div className="mp-edit-actions">
                    <button className="mp-edit-save" onClick={saveEdit}>{t("mypage.address.save")}</button>
                    <button className="mp-edit-cancel" onClick={cancelEdit}>{t("mypage.address.cancel")}</button>
                  </div>
                </div>
              ) : (
                /* 보기 모드 */
                <>
                  <div className="mp-addr-header">
                    <div className="mp-addr-title-row">
                      <strong>{a.label}</strong>
                      {a.is_default && <span className="mp-addr-default">{t("mypage.address.defaultBadge")}</span>}
                    </div>
                    <div className="mp-addr-btns">
                      <button className="mp-addr-edit-btn" onClick={() => startEdit(a)}>{t("mypage.address.edit")}</button>
                      <button className="mp-addr-del" onClick={() => handleDelete(a.id)}>{t("mypage.address.delete")}</button>
                    </div>
                  </div>
                  <div className="mp-addr-info">
                    <div className="mp-addr-info-row">
                      <span className="mp-addr-label">{t("mypage.address.name")}</span>
                      <span className="mp-addr-value">{a.customer_name}</span>
                    </div>
                    <div className="mp-addr-info-row">
                      <span className="mp-addr-label">{t("mypage.address.phone")}</span>
                      <span className="mp-addr-value">{a.customer_phone}</span>
                    </div>
                    <div className="mp-addr-info-row">
                      <span className="mp-addr-label">{t("mypage.address.address")}</span>
                      <span className="mp-addr-value">
                        {a.shipping_type === "domestic" ? (
                          <>{a.zipcode && `(${a.zipcode}) `}{a.address} {a.address_detail}</>
                        ) : (
                          <>{a.address} {a.address_detail}, {a.city} {a.state} {a.zipcode}, {a.country}</>
                        )}
                      </span>
                    </div>
                    {a.delivery_memo && (
                      <div className="mp-addr-info-row">
                        <span className="mp-addr-label">{t("mypage.address.memo")}</span>
                        <span className="mp-addr-value">{a.delivery_memo}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
